import { Injectable } from '@angular/core';
import { DateTime, DurationLike } from 'luxon';
import { Observable, of, switchMap } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { FormGroup } from '@angular/forms';

export interface FormProgress {
  lastUpdated: Date,
  formValue: any
}

@Injectable({
  providedIn: 'root'
})
export class FormProgressService {

  constructor(
    private storage: LocalStorageService
  ) { }


  
  /**
   * Load form progress from local storage.  Mutates the passed in form with the saved values
   * @param key 
   * @param form 
   * @param maxAge How long to check back for saved progress.  Defaults to 7 days.
   * @returns 
   */
  loadFormProgress(key: string, form: FormGroup<any>, maxAge: DurationLike = {days: 7}): Observable<FormProgress | null> {

    return this.getFormProgress(key).pipe(
      switchMap(progress => {
        if (progress &&  progress.lastUpdated > DateTime.now().minus(maxAge).toJSDate())  {
          form.patchValue(progress.formValue, {emitEvent: false});
          return of(progress);
        } else {
          //if no progress or progress was expired, clear any existing value and return null
          return this.clearFormProgress(key).pipe(
            switchMap(() => of(null))
          )
         
        }
      })

    )


  }



  getFormProgress(key: string): Observable<FormProgress> {
    const progress = this.storage.getObject(key);
    if (progress) {
      progress.lastUpdated = DateTime.fromISO(progress.lastUpdated as unknown as string).toJSDate();

      
      this.updateDateObjects(progress.formValue)
    }


    return of(progress);
  }

  updateDateObjects(obj: any) {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const formField = obj[key];
        if (typeof formField === "object") {
          this.updateDateObjects(formField)
        }
        else {
          //convert dates back into JS dates
          //if someone puts a valid date string in a textbox, this will technically convert it...
          //A little hacky, but prevents converting numbers, booleans, and strings that are valid for the date constructor to dates
          if (typeof formField === "string" && formField.endsWith("Z") && !isNaN(new Date(formField).valueOf())) {
            obj[key] = new Date(formField);
          }
        }

      }
    }
  }

  saveFormProgress(key: string, formValue: any): Observable<FormProgress> {
    this.storage.setObject(key, { lastUpdated: new Date(), formValue: formValue });
    return of(this.storage.getObject(key));
  }

  clearFormProgress(key: string): Observable<boolean> {
    this.storage.removeItem(key);
    return of(true)
  }

}
